import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import MaskedInput from "react-text-mask";
import Actions from "@redux/app";
import { MASK_VALUE } from "@constants";
import { StateSelection } from "@components/common";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { GenericService, AccountService } from "@services";
import utils from "@utils";
import { ReactSelect, Switch } from "../../../components";

class UserInformation extends Component {
	constructor(props) {
		super(props);

		const { user } = props;

		this.state = {
			form: this.fillUserData(user),
			validations: {
				FirstName: false, // true = has error
				LastName: false,
				StreetAddress: false,
				StateId: false,
				City: false,
				Zip: false,
				TimeZoneId: false,
				CellPhone: false,
			},
			timeZones: {
				data: [],
				loading: false,
			},
			useMilitaryTime: user.UserPreferences != null,
			loadingSubmit: false,
			showSubmit: false,
		};
	}

	componentDidMount() {
		this.getTimeZones();
	}

	/**
	 * Gathers all requierd infromatoin related to the user
	 * @param {Object} user The required user data
	 */
	fillUserData = user => ({
		FirstName: user.FirstName,
		LastName: user.LastName,
		StreetAddress: user.StreetAddress,
		CellPhone: user.CellPhone,
		City: user.City,
		StateId: user.StateId,
		Zip: user.Zip,
		TimeZoneId: user.TimeZoneId,
		UserPreferences: user.UserPreferences,
	});
	// Military time is currently hidden

	/** Gets the most up to date time zone for the user */
	getTimeZones = () => {
		const { lang } = global;

		this.setState(({ timeZones }) => ({ timeZones: { ...timeZones, loading: true } }));

		GenericService.getTimeZones()
			.then(({ data }) => {
				const timeZones = data.map(item => ({
					value: item.Id,
					label: item.Description,
				}));

				this.setState({ timeZones: { data: timeZones, loading: false } });
			})
			.catch(({ response }) => {
				utils.notify({
					type: "error",
					message: response.data.Message || lang.t("Error loading Time Zones"),
				});

				this.setState(({ timeZones }) => ({ timeZones: { ...timeZones, loading: false } }));
			});
	};

	/**
	 * Validates and submits all required information for making changes to the user information
	 * @param {Event} event An event used to prevent event bubbling
	 */
	onSubmit = event => {
		if (event) event.preventDefault();

		const { lang } = global;
		const { user, receiveUser } = this.props;
		const { form, validations } = this.state;

		// Validate form
		const newValidations = { ...validations };

		newValidations.FirstName = form.FirstName === "";
		newValidations.LastName = form.LastName === "";
		newValidations.StreetAddress = form.StreetAddress === "";
		newValidations.CellPhone = form.CellPhone === "" || utils.revertPhoneMask(form.CellPhone).length !== 10;
		newValidations.City = form.City === "";
		newValidations.StateId = form.StateId === "";
		newValidations.Zip = form.Zip === "";
		newValidations.TimeZoneId = form.TimeZoneId === "";

		// If any validation property is true, the form is not valid
		const validationError = Object.values(newValidations).some(item => item);

		this.setState({
			validations: newValidations,
			loadingSubmit: !validationError,
		});

		if (validationError) return;

		AccountService.updateAccount({
			...this.fillUserData(form),
			OrganizationEmail: user.Organization.OrganizationEmail,
		})
			.then(({ data }) => {
				utils.notify({
					type: "success",
					message: lang.t("User successfully updated"),
				});

				this.setState({
					form: this.fillUserData(data),
					loadingSubmit: false,
					showSubmit: false,
				});

				// Update user object in the redux store
				receiveUser(data);
			})
			.catch(({ response }) => {
				utils.notify({
					type: "error",
					message: response.data.Message || lang.t("Error updating user"),
				});

				this.setState({ loadingSubmit: false });
			});
	};

	handleToggleMilitaryTime = () => {
		const { user } = this.props;
		const { useMilitaryTime } = this.state;
		const data = this.fillUserData(user);
		if (!useMilitaryTime) {
			data.UserPreferences = "useMilitaryTime";
		} else {
			data.UserPreferences = null;
		}
		this.setState(prevState => ({
			useMilitaryTime: !prevState.useMilitaryTime,
			showSubmit: true,
			form: data,
		}));
	};

	onCancel = () => {
		const { user } = this.props;

		// Reset form with original user data
		// Reset validations with false
		this.setState(({ validations }) => ({
			form: this.fillUserData(user),
			validations: Object.fromEntries(
				Object.entries(validations).map(([key]) => [key, false]),
			),
			showSubmit: false,
		}));
	};

	onFormChange = property => event => {
		const value = event?.target?.value ?? event?.value ?? "";

		this.setState(state => ({
			form: {
				...state.form,
				[property]: value,
			},
			showSubmit: true,
		}));
	};

	render() {
		const { lang } = global;
		const { user } = this.props;
		const { form, validations, timeZones, loadingSubmit, showSubmit, useMilitaryTime } =
			this.state;

		return (
			<div className="my-account-panel">
				<div className="my-account-panel-header">
					<h4 className="title">{lang.t("User Information")}</h4>
					<h5 className="subtitle">{lang.t("Update name, address and timezone")}</h5>
				</div>
				<div className="my-account-panel-body">
					<div className="row form-group">
						<div className="col-xs-12 col-sm-3">
							<label htmlFor="email">{`${lang.t("EMail")}:`}</label>
						</div>
						<div className="col-xs-12 col-sm-8 text-left">
							<strong>{user.Email}</strong>
						</div>
					</div>

					<div className={`row form-group ${validations.FirstName ? "has-error" : ""}`}>
						<div className="col-xs-12 col-sm-3">
							<label htmlFor="FirstName">{`${lang.t("First Name")}:`}</label>
						</div>
						<div className="col-xs-12 col-sm-8">
							<input
								type="text"
								className="form-control"
								id="FirstName"
								value={form.FirstName}
								onChange={this.onFormChange("FirstName")}
							/>
						</div>
					</div>

					<div className={`row form-group ${validations.LastName ? "has-error" : ""}`}>
						<div className="col-xs-12 col-sm-3">
							<label htmlFor="LastName">{`${lang.t("Last Name")}:`}</label>
						</div>
						<div className="col-xs-12 col-sm-8">
							<input
								type="text"
								className="form-control"
								id="LastName"
								value={form.LastName}
								onChange={this.onFormChange("LastName")}
							/>
						</div>
					</div>

					<div
						className={`row form-group ${validations.StreetAddress ? "has-error" : ""}`}
					>
						<div className="col-xs-12 col-sm-3">
							<label htmlFor="StreetAddress">{`${lang.t("Address")}:`}</label>
						</div>
						<div className="col-xs-12 col-sm-8">
							<input
								type="text"
								className="form-control"
								id="StreetAddress"
								value={form.StreetAddress}
								onChange={this.onFormChange("StreetAddress")}
							/>
						</div>
					</div>

					<div className={`row form-group ${validations.CellPhone ? "has-error" : ""}`}>
						<div className="col-xs-12 col-sm-3">
							<label htmlFor="CellPhone">{`${lang.t("Phone")}:`}</label>
						</div>
						<div className="col-xs-12 col-sm-8">
							<MaskedInput
								id="CellPhone"
								type="text"
								mask={MASK_VALUE.phoneMask}
								className="form-control"
								value={form.CellPhone}
								onChange={this.onFormChange("CellPhone")}
							/>
						</div>
					</div>

					<div className={`row form-group ${validations.City ? "has-error" : ""}`}>
						<div className="col-xs-12 col-sm-3">
							<label htmlFor="City">{`${lang.t("City")}:`}</label>
						</div>
						<div className="col-xs-12 col-sm-8">
							<input
								type="text"
								className="form-control"
								id="City"
								value={form.City}
								onChange={this.onFormChange("City")}
							/>
						</div>
					</div>

					<div className={`row form-group ${validations.StateId ? "has-error" : ""}`}>
						<div className="col-xs-12 col-sm-3">
							<label htmlFor="StateId">{`${lang.t("State")}:`}</label>
						</div>
						<div className="col-xs-12 col-sm-8">
							<StateSelection
								value={form.StateId}
								id="StateId"
								name="StateId"
								onChange={this.onFormChange("StateId")}
							/>
						</div>
					</div>

					<div className={`row form-group ${validations.Zip ? "has-error" : ""}`}>
						<div className="col-xs-12 col-sm-3">
							<label htmlFor="Zip">{`${lang.t("Zip Code")}:`}</label>
						</div>
						<div className="col-xs-12 col-sm-8">
							<input
								type="text"
								className="form-control"
								id="Zip"
								value={form.Zip}
								onChange={this.onFormChange("Zip")}
							/>
						</div>
					</div>

					<div className={`row form-group ${validations.TimeZoneId ? "has-error" : ""}`}>
						<div className="col-xs-12 col-sm-3">
							<label htmlFor="TimeZoneId">{`${lang.t("Time Zone")}:`}</label>
						</div>
						<div className="col-xs-12 col-sm-8">
							<ReactSelect
								value={form.TimeZoneId}
								id="TimeZoneId"
								name="TimeZoneId"
								onChange={this.onFormChange("TimeZoneId")}
								options={timeZones.data}
								isLoading={timeZones.loading}
							/>
						</div>
					</div>

					<div className="row form-group">
						<div className="col-xs-12 col-sm-3">
							<label htmlFor="UseMilitaryTime">{`${lang.t("Use military time")}:`}</label>
						</div>
						<div className="col-xs-12 col-sm-8">
							<div className="my-account-switch-container">
								<Switch
									onClick={() => this.handleToggleMilitaryTime()}
									on={useMilitaryTime}
								/>
							</div>
						</div>
					</div>

					<div className={`my-account-submit-buttons ${showSubmit ? "" : "not-visible"}`}>
						<button
							type="button"
							id="my-account-user-info-save-btn"
							className="btn btn-primary"
							disabled={loadingSubmit}
							onClick={this.onSubmit}
						>
							{loadingSubmit ? lang.t("Loading") : lang.t("Update")}
						</button>
						<button
							type="button"
							id="my-account-user-info-cancel-btn"
							className="btn btn-link"
							disabled={loadingSubmit}
							onClick={this.onCancel}
						>
							{lang.t("Cancel")}
						</button>
					</div>
				</div>
			</div>
		);
	}
}

const mapDispatchToProps = dispatch => ({
	receiveUser: bindActionCreators(Actions, dispatch).receiveUser,
});

UserInformation.propTypes = {
	receiveUser: PropTypes.func.isRequired,
	user: PropTypes.object,
};

UserInformation.defaultProps = {
	user: {},
};

export default withLDConsumer()(connect(null, mapDispatchToProps)(UserInformation));
